import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';

import { getMpfAlteracoesRevisao } from 'volto-ministerio-publico-federal/actions';
import {
  type Base,
  type Marcacoes,
  type Trecho,
  marcarAlteracoes,
} from 'volto-ministerio-publico-federal/helpers/MarcacoesRevisao';
import { verboDaTransicao } from 'volto-ministerio-publico-federal/helpers/Notificacoes';

/** Quem decidiu (efetivou, publicou...) depois de alterar o texto. */
export type Decisao = {
  transicao: string;
  ator: string;
  ator_nome: string;
  data: string;
};

/** Quem atualizou por último a notícia já publicada no nacional. */
export type Atualizacao = { ator: string; ator_nome: string; data: string };

type Resposta = {
  ativo: boolean;
  estado?: string;
  base?: Base;
  decisao?: Decisao;
  atualizacao?: Atualizacao;
};

/**
 * Marca-texto da revisão na view da notícia. Só consulta o backend com
 * usuário logado; o backend só devolve a base a quem trabalha na notícia,
 * então o público nunca vê marcação (nas revisões nacionais a notícia está no ar).
 */
export const useMarcacoesRevisao = (content: any) => {
  const dispatch: any = useDispatch();
  const logado = useSelector((state: any) => !!state.userSession?.token);
  const [base, setBase] = useState<Base | null>(null);
  const [decisao, setDecisao] = useState<Decisao | null>(null);
  const [atualizacao, setAtualizacao] = useState<Atualizacao | null>(null);
  const [visivel, setVisivel] = useState(true);

  const path = content?.['@id'] ? flattenToAppURL(content['@id']) : '';
  const estado = content?.review_state;

  useEffect(() => {
    setBase(null);
    setDecisao(null);
    setAtualizacao(null);
    if (!logado || !path) return undefined;
    let ativo = true;
    dispatch(getMpfAlteracoesRevisao(path))
      .then((resposta: Resposta) => {
        if (ativo && resposta?.ativo && resposta.base) {
          setBase(resposta.base);
          setDecisao(resposta.decisao || null);
          setAtualizacao(resposta.atualizacao || null);
        }
      })
      .catch(() => {});
    return () => {
      ativo = false;
    };
  }, [dispatch, logado, path, estado]);

  const marcacoes: Marcacoes | null = useMemo(
    () => (base ? marcarAlteracoes(content, base) : null),
    [base, content],
  );

  return {
    marcacoes,
    desde: base?.data,
    decisao,
    atualizacao,
    visivel,
    alternar: () => setVisivel((v) => !v),
  };
};

/** Texto com os trechos alterados no marca-texto. */
export const TextoMarcado = ({ trechos }: { trechos: Trecho[] }) => (
  <>
    {trechos.map((trecho, i) =>
      trecho.removido ? (
        <del key={i} className="mpf-removido">
          {trecho.texto}
        </del>
      ) : trecho.alterado ? (
        <mark key={i} className="mpf-alterado">
          {trecho.texto}
        </mark>
      ) : (
        trecho.texto
      ),
    )}
  </>
);

const plural = (n: number, um: string, varios: string) =>
  `${n} ${n === 1 ? um : varios}`;

const formatarData = (iso?: string) => {
  if (!iso) return '';
  const data = new Date(iso);
  return isNaN(data.getTime())
    ? ''
    : data.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
};

/**
 * Faixa acima do título: quanto mudou desde o envio e o botão de ocultar.
 * Depois de uma decisão com alterações (`decisao`), diz quem decidiu: "Julia
 * Silva efetivou a publicação nacional com 2 trechos alterados". Na
 * publicada nacional atualizada no ar (`atualizacao`), diz quem atualizou.
 */
export const AvisoMarcacoes = ({
  marcacoes,
  desde,
  decisao,
  atualizacao,
  visivel,
  alternar,
}: {
  marcacoes: Marcacoes;
  desde?: string;
  decisao?: Decisao | null;
  atualizacao?: Atualizacao | null;
  visivel: boolean;
  alternar: () => void;
}) => {
  const { alterados, removidos } = marcacoes;
  const data = formatarData((decisao || atualizacao)?.data || desde);
  const partes = [];
  if (alterados)
    partes.push(plural(alterados, 'trecho alterado', 'trechos alterados'));
  if (removidos)
    partes.push(plural(removidos, 'trecho removido', 'trechos removidos'));
  return (
    <div className="mpf-revisao-aviso" role="status">
      <p className="mpf-revisao-aviso__texto">
        {atualizacao ? (
          <>
            <strong>Atualizações desde a publicação nacional:</strong>{' '}
            {atualizacao.ator_nome} atualizou a notícia
            {partes.length ? ` (${partes.join(' e ')})` : ''}
          </>
        ) : decisao ? (
          <>
            <strong>Alterações na validação:</strong> {decisao.ator_nome}{' '}
            {verboDaTransicao(decisao.transicao)}
            {partes.length ? ` com ${partes.join(' e ')}` : ''}
          </>
        ) : (
          <>
            <strong>Revisão:</strong>{' '}
            {partes.length
              ? `${partes.join(' e ')} desde o envio para revisão`
              : 'nenhuma alteração desde o envio para revisão'}
          </>
        )}
        {data ? ` (${data})` : ''}.
        {visivel && alterados > 0 && (
          <>
            {' '}
            Incluído: <mark className="mpf-alterado">assim</mark>.
          </>
        )}
        {visivel && removidos > 0 && (
          <>
            {' '}
            Excluído: <del className="mpf-removido">assim</del>.
          </>
        )}
      </p>
      {alterados + removidos > 0 && (
        <button
          type="button"
          className="mpf-revisao-aviso__botao"
          onClick={alternar}
          aria-pressed={!visivel}
        >
          {visivel ? 'Ocultar marcações' : 'Mostrar marcações'}
        </button>
      )}
    </div>
  );
};
